"use client";

import { useCallback, useId, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, Variants } from "framer-motion";
import Fade from "embla-carousel-fade";
import { CarouselApi, CarouselContent, CarouselItem } from "~/components/ui/carousel";
import { Carousel } from "~/componentsDS/Carousel";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { cn } from "~/lib/utils";
import MobileWeekNav from "./MobileWeekNav";
import SlideContent from "./SlideContent";
import TimelineNav from "./TimelineNav";
import { DEFAULT_TIMELINE_ANALYTIC_EVENT, OESTRA_COMPACT_STEPS, OESTRA_TIMELINE_STEPS } from "./data";
import { InteractiveTimelineProps } from "./types";

export type { ImageConfig, InteractiveTimelineStep, InteractiveTimelineProps, InteractiveTimelineVariant } from "./types";
export { OESTRA_TIMELINE_STEPS, OESTRA_COMPACT_STEPS } from "./data";

const mobileTextVariants: Variants = {
    hidden: { opacity: 0, y: 8 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
    },
    exit: {
        opacity: 0,
        y: -8,
        transition: { duration: 0.2, ease: "easeIn" },
    },
};

const InteractiveTimeline = ({
    steps = OESTRA_TIMELINE_STEPS,
    compactSteps = OESTRA_COMPACT_STEPS,
    analyticEvent = DEFAULT_TIMELINE_ANALYTIC_EVENT,
    variant = "default",
    className,
}: InteractiveTimelineProps) => {
    const panelId = useId();
    const [activeStep, setActiveStep] = useState(0);
    const [carouselApi, setCarouselApi] = useState<CarouselApi>();
    const animKeyRef = useRef(0);
    const prevTrackedIndexRef = useRef(0);
    const fadePlugins = useMemo(() => [Fade()], []);
    const isCompact = variant === "compact";
    const resolvedSteps = isCompact ? compactSteps : steps;

    const sendEvent = useTrackEvent();

    const handleStepClick = useCallback((index: number) => {
        animKeyRef.current += 1;
        setActiveStep(index);
        carouselApi?.scrollTo(index);
    }, [carouselApi]);

    const handleCarouselSelect = useCallback((api: CarouselApi) => {
        if (!api) return;

        const index = api.selectedScrollSnap();

        setActiveStep((prev) => {
            if (prev === index) return prev;
            animKeyRef.current += 1;
            return index;
        });

        if (prevTrackedIndexRef.current === index) return;

        const item = resolvedSteps[index];

        if (!item) return;

        prevTrackedIndexRef.current = index;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: analyticEvent.event_name,
                widget: analyticEvent.widget,
                meta_data: {
                    click_text: item.week,
                },
            },
        });
    }, [resolvedSteps, sendEvent, analyticEvent.event_name, analyticEvent.widget,]);

    const activeStepData = resolvedSteps[activeStep];

    return (
        <div className={cn(
            "grid grid-cols-1 gap-6",
            !isCompact && "lg:grid-cols-[396px_1fr] lg:gap-12 lg:items-stretch lg:mx-auto lg:max-w-[1600px] lg:px-12 xl:px-16 min-[1440px]:px-20",
            className,
        )}>
            {!isCompact && (
                <TimelineNav
                    steps={resolvedSteps}
                    activeStep={activeStep}
                    onStepClick={handleStepClick}
                    panelId={panelId}
                />
            )}

            <div className="flex flex-col gap-10 min-w-0">
                <Carousel
                    setApi={setCarouselApi}
                    onSelect={handleCarouselSelect}
                    plugins={fadePlugins}
                    loop={false}
                    arrows={false}
                    dots
                    variant="compact"
                    compactPaginationAlign="right"
                    compactPaginationBg="bg-trueBlack/10"
                    compactPaginationDotColor="bg-pureWhite"
                    className={cn(
                        !isCompact && "lg:rounded-3xl lg:overflow-hidden lg:bg-pureWhite lg:shadow-[-34px_28px_12px_0_rgba(168,168,168,0),_-22px_18px_11px_0_rgba(168,168,168,0.01),_-12px_10px_10px_0_rgba(168,168,168,0.04),_-5px_5px_7px_0_rgba(168,168,168,0.07),_-1px_1px_4px_0_rgba(168,168,168,0.08)]",
                        isCompact && "lg:[&>.absolute]:bottom-auto lg:[&>.absolute]:top-[46px] lg:[&>.absolute]:py-0",
                    )}
                >
                    <CarouselContent
                        id={panelId}
                        role="tabpanel"
                    >
                        {resolvedSteps.map((step, i) => (
                            <CarouselItem key={step.week} className={cn(
                                "min-w-0 aspect-[400/294] md:aspect-[836/560]",
                                isCompact ? "lg:aspect-auto lg:h-[280px]" : "lg:aspect-auto lg:h-[520px]",
                            )}>
                                <SlideContent
                                    step={step}
                                    index={i}
                                    isActive={activeStep === i}
                                    animKey={animKeyRef.current}
                                    isCompact={isCompact}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>

                <MobileWeekNav
                    steps={resolvedSteps}
                    activeStep={activeStep}
                    onStepClick={handleStepClick}
                    panelId={panelId}
                    alwaysVisible={isCompact}
                />

                <AnimatePresence mode="wait">
                    {activeStepData && (
                        <motion.div
                            key={activeStepData.week}
                            variants={mobileTextVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className={cn("px-6", !isCompact && "lg:hidden")}
                        >
                            <Heading
                                as="h3"
                                size="xs"
                                weight="400"
                                className="text-primary500 mb-4"
                            >
                                {activeStepData.nav.title}
                            </Heading>
                            {isCompact && activeStepData.nav.description && (
                                <Paragraph size="lg" className="hidden lg:block text-primary500">
                                    {activeStepData.nav.description}
                                </Paragraph>
                            )}
                            {activeStepData.nav.mobileDescription && (
                                <Paragraph size="lg" className={cn("text-primary500", isCompact && "lg:hidden")}>
                                    {activeStepData.nav.mobileDescription}
                                </Paragraph>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default InteractiveTimeline;
